/**
 * Wann ein Termin stattfindet - als EIN lesbarer Satz.
 *
 * ── Warum es diese Datei gibt ────────────────────────────────────────
 * Bis v0.20.2 stand an vier Stellen `formatDateTime(event.startAt)`: in der
 * Terminkarte, der Agenda-Zeile, der Detailseite und der öffentlichen Seite.
 * Die Endzeit war damit auf den Karten gar nicht zu sehen, und auf den beiden
 * Detailseiten stand sie als eigene Zeile „Bis 24. Oktober 2026, 22:00 Uhr"
 * darunter - das volle Datum ein zweites Mal, obwohl sich meist nur die
 * Uhrzeit unterscheidet. Wer wissen wollte, wie lange etwas geht, musste zwei
 * Zeilen vergleichen.
 *
 * ── Die zwei Fälle, die zusammengehören ──────────────────────────────
 * Ein Termin hat seit v0.20.2 eine Uhrzeit ODER keine (`allDay`). Beides an
 * jeder Anzeigestelle einzeln zu behandeln hiesse, dieselbe Fallunterscheidung
 * viermal zu schreiben - und beim fünften Ort zu vergessen. Deshalb eine
 * Funktion, die das ganze Ereignis nimmt und den fertigen Satz liefert:
 *
 *   ohne Uhrzeit          24. Oktober 2026
 *   ohne Uhrzeit, 3 Tage  24. Oktober 2026 – 26. Oktober 2026
 *   mit Uhrzeit           24. Oktober 2026, 18:00 Uhr
 *   mit Ende, selber Tag  24. Oktober 2026, 18:00 – 22:00 Uhr
 *   mit Ende, anderer Tag 24. Oktober 2026, 18:00 Uhr – 26. Oktober 2026, 12:00 Uhr
 *
 * Der häufigste Fall - abends von 18 bis 22 Uhr - ist damit eine Zeile, in der
 * das Datum genau einmal vorkommt.
 *
 * ── Bestandstermine ──────────────────────────────────────────────────
 * `allDay` fehlt an jedem Termin von vor v0.20.2. Ein fehlendes Feld bedeutet
 * deshalb „hat eine Uhrzeit" - so bleibt die Anzeige für alles Bestehende
 * exakt wie vorher, und es braucht keinen Migrationslauf.
 *
 * Reine Funktionen ohne Firebase-Import - aus Jest direkt testbar.
 */
import { formatDate, formatTime, relativeLabel, startOfToday, toDate } from './dateUtils';

// ── Wann ein Termin vorbei ist ──────────────────────────────────────
// Bis v0.36 hiess „vorbei": der START liegt vor heute. Ein Treffen ueber ein
// Wochenende fiel damit am Samstag aus allen Listen, obwohl es bis Sonntag
// lief (von Christian gemeldet, 27.09.2026). Seither zaehlt das ENDE - und
// ohne Ende wie bisher der Beginn. In ganzen Tagen: Ein Termin, der heute
// endet, ist heute noch da.

/** Wie weit die Terminabfragen zurueckgreifen, damit laufende Mehrtages-Termine mitkommen. */
export const RUNNING_LOOKBACK_DAYS = 31;

function dayStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Das Ende eines Termins - ohne Endangabe der Beginn. */
export function eventEnd(event) {
  return toDate(event?.endAt) ?? toDate(event?.startAt);
}

/** Noch nicht vorbei: Das Ende liegt heute oder spaeter. */
export function isEventUpcoming(event, now = new Date()) {
  const ende = eventEnd(event);
  return Boolean(ende) && dayStart(ende) >= dayStart(now);
}

/** Hat vor heute begonnen und laeuft noch - der Mehrtages-Fall. */
export function isEventRunning(event, now = new Date()) {
  const start = toDate(event?.startAt);
  return Boolean(start) && dayStart(start) < dayStart(now) && isEventUpcoming(event, now);
}

/** „Läuft" fuer einen laufenden Mehrtages-Termin, sonst das bekannte Etikett. */
export function eventRelativeLabel(event) {
  return isEventRunning(event) ? 'Läuft' : relativeLabel(event?.startAt);
}

/**
 * Untergrenze fuer die Firestore-Abfragen der Terminlisten.
 *
 * Abgefragt wird ueber `startAt` (nur darauf liegt der Index mit der
 * Sortierung) - ein Termin, der gestern begann und morgen endet, fiele bei
 * `startAt >= heute` schon aus der ABFRAGE. Deshalb greift sie
 * RUNNING_LOOKBACK_DAYS zurueck; was davon wirklich vorbei ist, sortiert
 * `isEventUpcoming` danach aus. Laenger laufende Termine gibt es im Club nicht.
 * Ueber einen Tag konstant, setzt den Listener also nicht bei jedem Render neu.
 */
export function upcomingQueryStart() {
  const tag = startOfToday();
  tag.setDate(tag.getDate() - RUNNING_LOOKBACK_DAYS);
  return tag;
}

/** Hat der Termin KEINE Uhrzeit? Fehlendes Feld = er hat eine. */
export function isAllDay(event) {
  return event?.allDay === true;
}

/** Zwei Zeitpunkte am selben Kalendertag? */
function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Der fertige Satz für die Anzeige. Leerer String, wenn kein gültiger Beginn
 * da ist - dieselbe Zusage wie bei `formatDate`/`formatDateTime`, damit ein
 * kaputter Datensatz eine leere Zeile ergibt statt „Invalid Date".
 */
export function formatEventWhen(event) {
  const start = toDate(event?.startAt);
  if (!start) return '';

  const end = toDate(event?.endAt);

  if (isAllDay(event)) {
    // Ohne Uhrzeit ist ein Ende am selben Tag keine Zusatzinformation - es
    // stünde zweimal dasselbe Datum da.
    if (end && !sameDay(start, end)) return `${formatDate(start)} – ${formatDate(end)}`;
    return formatDate(start);
  }

  if (!end) return `${formatDate(start)}, ${formatTime(start)} Uhr`;

  // Der Normalfall: ein Abend. Das Datum steht einmal, die Spanne dahinter.
  if (sameDay(start, end)) {
    return `${formatDate(start)}, ${formatTime(start)} – ${formatTime(end)} Uhr`;
  }

  return (
    `${formatDate(start)}, ${formatTime(start)} Uhr – ` +
    `${formatDate(end)}, ${formatTime(end)} Uhr`
  );
}
